import type { BoardFilterType } from "@repo/db/schema";
import type {
  FilterRelative,
  FilterType,
  RelativeIntervalType,
  RelativeModeType,
} from "@repo/shared/rift";

export const NUMBER_MODES: FilterType["mode"][] = [
  "EQUAL",
  "NOT_EQUAL",
  "GREATER",
  "GREATER_EQUAL",
  "LESS",
  "LESS_EQUAL",
];

export const STRING_MODES: FilterType["mode"][] = [
  "CONTAINS",
  "NOT_CONTAINS",
  "EQUAL",
  "NOT_EQUAL",
  "STARTS",
  "ENDS",
  "MATCHES",
  "EMPTY",
  "NOT_EMPTY",
];

export const DATE_MODES: FilterType["mode"][] = [
  "EQUAL",
  "NOT_EQUAL",
  "GREATER",
  "GREATER_EQUAL",
  "LESS",
  "LESS_EQUAL",
  "EMPTY",
  "NOT_EMPTY",
  "RELATIVE",
  "BETWEEN",
];

export const MODE_LABELS: Record<string, string> = {
  CONTAINS: "Contains",
  NOT_CONTAINS: "Does not contain",
  EQUAL: "Equal",
  NOT_EQUAL: "Not equal",
  STARTS: "Starts with",
  ENDS: "Ends with",
  MATCHES: "Matches regex",
  GREATER: "Greater than",
  GREATER_EQUAL: "Greater or equal",
  LESS: "Less than",
  LESS_EQUAL: "Less or equal",
  EMPTY: "Empty",
  NOT_EMPTY: "Not empty",
  RELATIVE: "Relative",
  BETWEEN: "Between",
};

/** Date-specific mode labels used in the date popover mode selector. */
export const DATE_MODE_LABELS: Record<string, string> = {
  EQUAL: "Is",
  NOT_EQUAL: "Is not",
  GREATER: "After",
  GREATER_EQUAL: "On or after",
  LESS: "Before",
  LESS_EQUAL: "On or before",
  EMPTY: "Empty",
  NOT_EMPTY: "Not empty",
  RELATIVE: "Relative",
  BETWEEN: "Between",
};

export const BOOLEAN_MODE_LABELS: Record<string, string> = {
  IS_TRUE: "True",
  IS_FALSE: "False",
  EMPTY: "Empty",
  NOT_EMPTY: "Not empty",
};

/** Friendly labels for relative mode (This / Last / Next). */
export const RELATIVE_MODE_LABELS: Record<RelativeModeType, string> = {
  PREVIOUS: "Last",
  CURRENT: "This",
  NEXT: "Next",
};

/** Friendly labels for relative interval (singular form). */
export const RELATIVE_INTERVAL_LABELS: Record<RelativeIntervalType, string> = {
  HOUR: "hour",
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
  QUARTER: "quarter",
  YEAR: "year",
};

/**
 * Formats the interval portion of a relative window into a human-readable phrase,
 * pluralizing when the value is not exactly one (e.g. "month", "3 months").
 */
export const formatRelativeIntervalPhrase = (
  relative: FilterRelative,
): string => {
  const intervalLabel = RELATIVE_INTERVAL_LABELS[relative.interval];
  return relative.value === 1
    ? intervalLabel
    : `${relative.value} ${intervalLabel}s`;
};

export const RELATIVE_MODES: RelativeModeType[] = [
  "PREVIOUS",
  "CURRENT",
  "NEXT",
];
export const RELATIVE_INTERVALS: RelativeIntervalType[] = [
  "HOUR",
  "DAY",
  "WEEK",
  "MONTH",
  "QUARTER",
  "YEAR",
];

export const VALUELESS_MODES = new Set<FilterType["mode"]>([
  "EMPTY",
  "NOT_EMPTY",
  "IS_TRUE",
  "IS_FALSE",
]);

export const BOARD_FILTER_TYPE_LABELS: Record<BoardFilterType, string> = {
  MATCH: "Match",
  SINGLE: "Single value",
  MULTI: "Multi select",
  DATE_RELATIVE: "Relative",
  DATE_DEFAULT: "Default",
};

/** Sentinel value used for NULL/empty string entries in value pickers. */
export const EMPTY_SENTINEL = "__EMPTY__";

export const VIRTUAL_ROW_HEIGHT = 28;
export const VIRTUAL_LIST_MAX_HEIGHT = 224;
